import type { RefObject } from "react";
import { useMotionValue, useReducedMotion, useSpring, useTransform } from "motion/react";
import type { CardEffects } from "./use-card-effects.types";

const TILT_DEG = 8;
const PULL_PX = 6;
const POINTER_SPRING = { stiffness: 150, damping: 18 };
const FADE_SPRING = { stiffness: 200, damping: 30 };

// Consumer owns the ref so we don't return a ref-bearing object (which would
// trip react-hooks-js/refs when consumers spread the result).
export function useCardEffects(ref: RefObject<HTMLLabelElement | null>): CardEffects {
  const reduceMotion = useReducedMotion();

  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const hover = useMotionValue(0);
  const sx = useSpring(px, POINTER_SPRING);
  const sy = useSpring(py, POINTER_SPRING);
  const glow = useSpring(hover, FADE_SPRING);
  const rotateX = useTransform(sy, (v) => -v * TILT_DEG);
  const rotateY = useTransform(sx, (v) => v * TILT_DEG);
  const pullX = useTransform(sx, (v) => v * PULL_PX);
  const pullY = useTransform(sy, (v) => v * PULL_PX);
  const heatImage = useTransform(
    [sx, sy],
    ([x = 0, y = 0]: number[]) =>
      `radial-gradient(120% 100% at ${50 + x * 60}% ${50 + y * 60}%, oklch(0.70 0.30 332 / 0.55), oklch(0.85 0.18 85 / 0.45) 30%, oklch(0.78 0.20 195 / 0.40) 55%, oklch(0.55 0.27 268 / 0.20) 78%, transparent 95%)`,
  );
  const rimImage = useTransform(
    [sx, sy],
    ([x = 0, y = 0]: number[]) =>
      `radial-gradient(75% 75% at ${50 + x * 120}% ${50 + y * 120}%, rgba(255,255,255,0.85), rgba(255,255,255,0.2) 45%, transparent 70%)`,
  );
  const expiryColor = useTransform(
    glow,
    (v) =>
      `color-mix(in oklab, var(--color-surface-foreground) ${v * 100}%, var(--color-surface-foreground-weak))`,
  );

  function onPointerMove(e: React.PointerEvent<HTMLLabelElement>) {
    const el = ref.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    px.set((e.clientX - rect.left) / rect.width - 0.5);
    py.set((e.clientY - rect.top) / rect.height - 0.5);
    hover.set(1);
  }

  function onPointerLeave() {
    px.set(0);
    py.set(0);
    hover.set(0);
  }

  if (reduceMotion) {
    return { pointerProps: {}, glow, heatImage, rimImage, expiryColor };
  }

  return {
    pointerProps: { onPointerMove, onPointerEnter: () => hover.set(1), onPointerLeave },
    tiltStyle: { rotateX, rotateY, x: pullX, y: pullY, transformPerspective: 700 },
    glow,
    heatImage,
    rimImage,
    expiryColor,
  };
}
